import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, AlertCircle, Save, RotateCcw,
  Plus, Trash2, Info, Percent, Clock, Award,
} from 'lucide-react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLoyaltyConfig } from '../hooks/useLoyalty';

export default function LoyaltyConfigForm() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const {
    config, loading, saving, loadError, dirty, validation,
    updateField, updateTier, addTier, removeTier, resetToDefaults, handleSave,
  } = useLoyaltyConfig();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        <span className="ml-3 text-gray-600">{t('loyalty.loadingText')}</span>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-red-800">{t('loyalty.loadFailTitle')}</h3>
          <p className="text-sm text-red-700 mt-1">{loadError || t('loyalty.loadFailFallback')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

      {/* Header */}
      <div className="flex flex-col">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-blue-500" />
            {t('loyalty.title')}
          </h1>
        </div>
        <p className="text-gray-600 mt-1">{t('loyalty.subtitle')}</p>
      </div>

      {/* Uyarı banner */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex">
          <Info className="h-5 w-5 text-blue-400 flex-shrink-0" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">{t('loyalty.infoBannerTitle')}</h3>
            <div className="mt-1 text-sm text-blue-700" dangerouslySetInnerHTML={{ __html: t('loyalty.infoBannerText') }} />
          </div>
        </div>
      </div>

      {/* Validation hataları */}
      {!validation.isValid && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                {t('loyalty.validationTitle', { count: validation.errors.length })}
              </h3>
              <ul className="mt-2 text-sm text-red-700 list-disc pl-5 space-y-1">
                {validation.errors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Genel Ayarlar */}
      <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Percent className="h-5 w-5 text-blue-500" />
          {t('loyalty.generalTitle')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('loyalty.earnRateLabel')}</label>
            <input type="number" step="0.01" min="0" value={config.earnRate}
              onChange={(e) => updateField('earnRate', Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-[11px] text-gray-500 mt-1" style={{ whiteSpace: 'pre-line' }}>{t('loyalty.earnRateHint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('loyalty.redeemRateLabel')}</label>
            <input type="number" step="0.01" min="0" value={config.redeemRate}
              onChange={(e) => updateField('redeemRate', Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-[11px] text-gray-500 mt-1" style={{ whiteSpace: 'pre-line' }}>{t('loyalty.redeemRateHint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('loyalty.minRedeemLabel')}</label>
            <input type="number" step="1" min="0" value={config.minRedeem}
              onChange={(e) => updateField('minRedeem', Math.floor(Number(e.target.value) || 0))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-[11px] text-gray-500 mt-1">{t('loyalty.minRedeemHint')}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {t('loyalty.expirationLabel')}
            </label>
            <input type="number" step="1" min="1" value={config.expirationMonths}
              onChange={(e) => updateField('expirationMonths', Math.floor(Number(e.target.value) || 1))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <p className="text-[11px] text-gray-500 mt-1">{t('loyalty.expirationHint')}</p>
          </div>
        </div>
      </div>

      {/* Tier Düzenleyici */}
      <div className="bg-white shadow-md rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Award className="h-5 w-5 text-blue-500" />
            {t('loyalty.tierTitle')}
          </h2>
          <button type="button" onClick={addTier}
            className="flex items-center bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-3 py-2 rounded-md text-sm hover:from-cyan-600 hover:to-blue-600 transition-colors">
            <Plus className="h-4 w-4 mr-1" />
            {t('loyalty.addTierBtn')}
          </button>
        </div>
        <p className="text-xs text-gray-500">{t('loyalty.tierHint')}</p>
        <div className="space-y-3">
          {config.tiers.map((tier, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full border-2 border-white shadow-sm flex-shrink-0"
                    style={{ backgroundColor: tier.color || '#888' }} />
                  <span className="text-sm font-semibold text-gray-700 capitalize">
                    {t('loyalty.tierLabel', { num: idx + 1, name: tier.name || t('loyalty.tierNoName') })}
                  </span>
                </div>
                <button type="button" onClick={() => removeTier(idx)} disabled={config.tiers.length <= 1}
                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title={config.tiers.length <= 1 ? t('loyalty.removeTierDisabledTitle') : t('loyalty.removeTierTitle')}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('loyalty.tierNameLabel')}</label>
                  <input type="text" value={tier.name || ''}
                    onChange={(e) => updateTier(idx, 'name', e.target.value.trim())}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('loyalty.tierMinLifetimeLabel')}</label>
                  <input type="number" step="1" min="0" value={tier.minLifetime}
                    onChange={(e) => updateTier(idx, 'minLifetime', Math.floor(Number(e.target.value) || 0))}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('loyalty.tierMultiplierLabel')}</label>
                  <input type="number" step="0.1" min="0.1" value={tier.multiplier}
                    onChange={(e) => updateTier(idx, 'multiplier', Number(e.target.value) || 1)}
                    className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('loyalty.tierColorLabel')}</label>
                  <div className="flex gap-1.5">
                    <input type="color" value={tier.color || '#888888'}
                      onChange={(e) => updateTier(idx, 'color', e.target.value)}
                      className="h-[32px] w-12 cursor-pointer border border-gray-300 rounded-md bg-white" />
                    <input type="text" value={tier.color || ''}
                      onChange={(e) => updateTier(idx, 'color', e.target.value)}
                      placeholder="#CD7F32"
                      className="flex-1 px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white font-mono" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action bar */}
      <div className="sticky bottom-0 bg-white shadow-md rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <button type="button" onClick={resetToDefaults}
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors border border-gray-200">
          <RotateCcw className="h-4 w-4" />
          {t('loyalty.resetBtn')}
        </button>
        <div className="flex gap-3">
          <button type="button" onClick={() => navigate('/home')}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
            {t('loyalty.cancelBtn')}
          </button>
          <button type="button" onClick={handleSave} disabled={saving || !validation.isValid || !dirty}
            className="flex items-center bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-5 py-2 rounded-md text-sm hover:from-cyan-600 hover:to-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Save className="h-4 w-4 mr-1" />
            {saving ? t('loyalty.savingBtn') : t('loyalty.saveBtn')}
          </button>
        </div>
      </div>
    </div>
  );
}
